import {formatOrderCar} from '../shared/order-car-display';
import {ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {environment} from '../../environments/environment';
import {DataService} from '../services/data.service';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {getOrderSourceLabel} from '../services/order-source-labels';
import {TableSortState} from '../table-sort';
import {GoogleMap, MapAdvancedMarker, MapInfoWindow} from '@angular/google-maps';
import {
    Address,
    ApiResponse,
    CompleteOrder,
    Driver,
    NewOrderRequest,
    Order,
    PendingVoucher,
    PendingVouchersResponse
} from '../models/api.models';
import {CoordinateLike} from '../models/ui.models';
import {PermissionService} from '../services/permission.service';
import {AdminPermissions} from '../models/admin-permissions';
import {createRouteMarkerContent} from '../shared/advanced-marker-content';
import {DialogService} from '../services/dialog.service';

interface SelectedOrderSummary {
    identifier: string;
    started?: string;
    finished?: string;
    state: string;
    platform: string;
    customer: string;
    assignedCar: string;
    carNumber: string;
    driverName: string;
    driverPhone: string;
    price: string;
    distance: string;
}

interface VoucherRoutePoint {
    label: string;
    address: Address;
    marker: string;
    type: string;
    formattedAddress?: string;
}

interface VoucherMapMarker extends google.maps.LatLngLiteral {
    position: google.maps.LatLngLiteral;
    content: HTMLElement;
    zIndex: number;
    title: string;
}

interface ResponsePayload extends Partial<ApiResponse> {
    error?: unknown;
    message?: string;
    Message?: string;
}

@Component({
    selector: 'app-vouchers',
    templateUrl: './vouchers.component.html',
    styleUrls: ['./vouchers.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VouchersComponent implements OnInit, OnDestroy {

    @ViewChild('voucherOrderMap', {static: false}) voucherOrderMap: GoogleMap;
    @ViewChild('voucherInfoWindow', {read: MapInfoWindow, static: false}) voucherInfoWindow: MapInfoWindow;

    pendingVouchers: PendingVoucher[] = [];
    filteredVouchers: PendingVoucher[] = [];
    totalCount = 0;
    carOptions: string[] = [];
    private carOptionsKey?: string;
    readonly voucherStatuses = ['Pending', 'Paid', 'Deleted', 'All'] as const;
    statusFilter = 'Pending';
    searchText = '';
    carFilter = '';
    private vouchersSubscription?: Subscription;
    private searchTimer?: ReturnType<typeof setTimeout>;
    currentPage = 1;
    pageSize = 25;
    selectedVoucher?: PendingVoucher;
    selectedVoucherSource?: PendingVoucher;
    editableVoucherIds = '';
    voucherEditMode = '';
    activeVoucherTab = 'payment';
    selectedVoucherOrderWrapper?: CompleteOrder;
    selectedOrderSummary?: SelectedOrderSummary;
    selectedOrderRoutePoints: VoucherRoutePoint[] = [];
    selectedOrderMapMarkers: VoucherMapMarker[] = [];
    selectedOrderMapLat = 44.429021;
    selectedOrderMapLng = 26.103849;
    selectedOrderMapCenter: google.maps.LatLngLiteral = {
        lat: this.selectedOrderMapLat,
        lng: this.selectedOrderMapLng
    };
    selectedOrderMapZoom = 13;
    readonly selectedOrderMapOptions: google.maps.MapOptions = {
        gestureHandling: 'greedy',
        mapId: environment.googleMapsMapId,
        scrollwheel: true
    };
    selectedOrderInfoMarker?: VoucherMapMarker;
    payMessage = '';
    payMessageType = '';
    isLoadingVouchers = true;
    isPayingVoucher = false;
    canPayVoucher = false;
    canDeletePendingVouchers = false;
    deletingVoucherId = '';
    imageZoom = 1;
    tableSort = new TableSortState();
    isDraggingPhoto = false;
    private photoDragStartX = 0;
    private photoDragStartY = 0;
    private photoDragScrollLeft = 0;
    private photoDragScrollTop = 0;
    private shouldFitSelectedOrderMap = false;

    private dataChangedSessionSubscription: Subscription;

    constructor(
        private dataService: DataService,
        private restService: RestService,
        private uiBlockerService: UIBlockerService,
        private permissionService: PermissionService,
        private dialogService: DialogService
    ) {
    }

    ngOnInit() {
        this.updatePermissions();
        this.dataChangedSessionSubscription = this.dataService.dataChangedSession.subscribe(() => {
            this.updatePermissions();
            this.loadPendingVouchers(true);
        });

        this.loadPendingVouchers();
    }

    ngOnDestroy() {
        if (!isNullOrUndefined(this.dataChangedSessionSubscription)) {
            this.dataChangedSessionSubscription.unsubscribe();
        }
        this.stopPhotoDrag();
        this.vouchersSubscription?.unsubscribe();
        clearTimeout(this.searchTimer);
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        if (this.dialogService.isOpen || isNullOrUndefined(this.selectedVoucher)) {
            return;
        }

        event.preventDefault();
        this.closeDetails();
    }

    @HostListener('document:mouseup')
    onDocumentMouseUp() {
        this.stopPhotoDrag();
    }

    loadPendingVouchers(refreshOptions = false) {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            this.isLoadingVouchers = false;
            return;
        }

        this.vouchersSubscription?.unsubscribe();
        const optionsKey = JSON.stringify([this.statusFilter, this.searchText.trim()]);
        const includeOptions = refreshOptions || optionsKey !== this.carOptionsKey;
        this.isLoadingVouchers = true;
        this.vouchersSubscription = this.restService.getPendingVouchers(
            this.currentPage, this.pageSize,
            [this.statusFilter], this.searchText,
            this.carFilter, '', this.tableSort.column, this.tableSort.direction, includeOptions)
            .pipe(
                finalize(() => {
                    this.isLoadingVouchers = false;
                    this.uiBlockerService.unBlockUI();
                })
            )
            .subscribe((res) => {
                this.pendingVouchers = this.getVouchersFromResponse(res);
                this.filteredVouchers = this.pendingVouchers;
                this.totalCount = res.TotalCount || 0;
                this.currentPage = res.Page || 1;
                if (includeOptions && this.dataService.isResponseSuccessful(res)) {
                    this.carOptions = (res.CarOptions || [])
                        .sort((left, right) => left.localeCompare(right, undefined, {numeric: true}));
                    this.carOptionsKey = optionsKey;
                    if (this.carFilter !== '' && !this.carOptions.includes(this.carFilter)) {
                        this.carFilter = '';
                        this.currentPage = 1;
                        this.loadPendingVouchers();
                    }
                }
            });
    }

    onSearchTextChanged(value: string) {
        this.searchText = isNullOrUndefined(value) ? '' : value;
        this.carFilter = '';
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.filterVouchers(true), 300);
    }

    filterVouchers(resetPage: boolean) {
        clearTimeout(this.searchTimer);
        if (resetPage) this.currentPage = 1;
        this.loadPendingVouchers();
    }

    toggleSort(column: string) {
        this.tableSort.toggle(column);
        this.filterVouchers(true);
    }

    onCarFilterChanged(value: string) {
        this.carFilter = value;
        this.filterVouchers(true);
    }

    onStatusFilterChanged(status: string) {
        this.statusFilter = status;
        this.carFilter = '';
        this.carOptions = [];
        this.carOptionsKey = undefined;
        this.filterVouchers(true);
    }

    getVoucherStatus(voucher: PendingVoucher): string {
        return voucher.Deleted ? 'Deleted' : voucher.IsPayed ? 'Paid' : 'Pending';
    }

    getCarFilterOptions() {
        return this.carOptions;
    }

    getPagedVouchers() {
        return this.filteredVouchers;
    }

    changePageSize(value: number) {
        this.pageSize = value;
        this.filterVouchers(true);
    }

    getTotalPages() {
        return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    }

    getFirstItemNumber() {
        return this.filteredVouchers.length === 0 ? 0 : ((this.currentPage - 1) * this.pageSize) + 1;
    }

    getLastItemNumber() {
        return Math.min(this.currentPage * this.pageSize, this.totalCount);
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.getTotalPages()) {
            this.currentPage = page;
            this.loadPendingVouchers();
        }
    }

    hasOrder(voucher: PendingVoucher | undefined) {
        return !isNullOrUndefined(voucher)
            && !isNullOrUndefined(voucher.OrderIdentifier)
            && String(voucher.OrderIdentifier).trim() !== '';
    }

    getVoucherCarIdentifier(voucher: PendingVoucher | undefined): string {
        const identifier = this.getVoucherCarFilterValue(voucher);
        return identifier === '' ? '-' : identifier;
    }

    openDetails(voucher: PendingVoucher) {
        this.selectedVoucherSource = voucher;
        this.selectedVoucher = Object.assign({}, voucher);
        this.voucherEditMode = this.getVoucherEditMode(voucher);
        this.editableVoucherIds = this.formatVoucherIds(voucher);
        this.activeVoucherTab = 'payment';
        this.setSelectedOrderData(voucher);
        this.clearPayMessage();
        this.imageZoom = 1;
        this.stopPhotoDrag();
    }

    onDetailsBackdropClick(event: MouseEvent) {
        if (event.target === event.currentTarget) {
            this.closeDetails();
        }
    }

    payVoucher(voucher: PendingVoucher | undefined) {
        if (!this.canPayVoucher || isNullOrUndefined(voucher) || this.getVoucherStatus(voucher) !== 'Pending' || this.isPayingVoucher
            || this.deletingVoucherId !== '') {
            return;
        }

        this.isPayingVoucher = true;
        this.clearPayMessage();
        const voucherToPay = this.buildPayVoucherPayload(voucher);
        this.restService.payPendingVoucher(voucherToPay)
            .pipe(
                finalize(() => {
                    this.isPayingVoucher = false;
                    this.uiBlockerService.unBlockUI();
                })
            )
            .subscribe({
                next: (res) => {
                    if (this.isPayResponseSuccessful(res)) {
                        this.resetSelectedVoucher();
                        this.loadPendingVouchers(true);
                    } else {
                        this.setPayMessage(this.getResponseMessage(res, 'Failed to pay voucher.'), 'error');
                    }
                },
                error: (error: unknown) => {
                    this.setPayMessage(this.getResponseMessage(error, 'Failed to pay voucher.'), 'error');
                }
            });
    }

    async deleteVoucher(voucher: PendingVoucher | undefined, event?: Event): Promise<void> {
        event?.stopPropagation();
        if (!this.canDeletePendingVouchers || isNullOrUndefined(voucher)
            || this.getVoucherStatus(voucher) !== 'Pending'
            || this.isPayingVoucher || this.deletingVoucherId !== '') return;

        const voucherId = isNullOrUndefined(voucher.Id) ? '' : String(voucher.Id).trim();
        if (voucherId === '') {
            await this.showDeleteError('The pending voucher could not be deleted.');
            return;
        }

        const confirmed = await this.dialogService.confirm({
            headerText: 'Delete pending voucher',
            bodyText: 'Are you sure you want to delete this pending voucher? It will be hidden from the pending vouchers list.',
            confirmButtonText: 'Delete voucher',
            cancelButtonText: 'Cancel',
            danger: true
        });
        if (!confirmed) return;

        this.deletingVoucherId = voucherId;
        this.restService.deletePendingVoucher(voucherId)
            .pipe(finalize(() => this.deletingVoucherId = ''))
            .subscribe({
                next: response => {
                    if (!this.dataService.isResponseSuccessful(response)) {
                        void this.showDeleteError(this.getResponseMessage(
                            response, 'The pending voucher could not be deleted.'));
                        return;
                    }

                    this.pendingVouchers = this.pendingVouchers.map(item =>
                        item.Id === voucherId ? {...item, Deleted: true} : item);
                    if (this.selectedVoucher?.Id === voucherId) this.resetSelectedVoucher();
                    this.loadPendingVouchers(true);
                },
                error: error => void this.showDeleteError(this.getResponseMessage(
                    error, 'The pending voucher could not be deleted.'))
            });
    }

    isDeletingVoucher(voucher: PendingVoucher | undefined): boolean {
        return !isNullOrUndefined(voucher) && this.deletingVoucherId !== ''
            && voucher.Id === this.deletingVoucherId;
    }

    closeDetails() {
        if (this.isPayingVoucher || this.deletingVoucherId !== '') {
            return;
        }

        this.resetSelectedVoucher();
    }

    getVoucherImageUrl(voucher: PendingVoucher | undefined) {
        if (isNullOrUndefined(voucher) || isNullOrUndefined(voucher.FileName) || voucher.FileName === '') {
            return '';
        }

        if (voucher.FileName.indexOf('https://') === 0
            || voucher.FileName.indexOf('data:') === 0) {
            return voucher.FileName;
        }

        if (voucher.FileName.indexOf('http:') === 0) {
            const imageUrl = new URL(voucher.FileName);
            imageUrl.protocol = 'https:';
            return imageUrl.toString();
        }

        return `${environment.baseUrl}/soc-driver/v2/images/${encodeURIComponent(voucher.FileName)}`;
    }

    hasVoucherImage(voucher: PendingVoucher | undefined) {
        return this.getVoucherImageUrl(voucher) !== '';
    }

    getVoucherIds(voucher: PendingVoucher | undefined): string[] {
        if (isNullOrUndefined(voucher) || isNullOrUndefined(voucher.VoucherIds)) {
            return [];
        }

        return voucher.VoucherIds;
    }

    openSelectedOrderMapInfo(mapMarker: MapAdvancedMarker, marker: VoucherMapMarker) {
        this.selectedOrderInfoMarker = marker;
        this.voucherInfoWindow.open(mapMarker);
    }

    private updatePermissions() {
        this.canPayVoucher = this.permissionService.has(AdminPermissions.PayVoucher);
        this.canDeletePendingVouchers = this.permissionService.has(AdminPermissions.DeletePendingVouchers);
    }

    getAmount(value: unknown) {
        const amount = Number(value);
        if (isNaN(amount)) {
            return '-';
        }

        return amount.toFixed(2);
    }

    getVoucherOrderWrapper(voucher: PendingVoucher | undefined): CompleteOrder | undefined {
        if (isNullOrUndefined(voucher)) {
            return undefined;
        }

        return voucher.Order || voucher.CompleteOrder || voucher.OrderDetails || voucher.OrderInfo || undefined;
    }

    getOrder(orderWrapper: CompleteOrder | undefined): Order | undefined {
        if (isNullOrUndefined(orderWrapper)) {
            return undefined;
        }

        return orderWrapper.Order;
    }

    zoomIn() {
        if (this.imageZoom < 3) {
            this.imageZoom = Math.round((this.imageZoom + 0.25) * 100) / 100;
        }
    }

    zoomOut() {
        if (this.imageZoom > 0.5) {
            this.imageZoom = Math.round((this.imageZoom - 0.25) * 100) / 100;
        }
    }

    resetZoom() {
        this.imageZoom = 1;
    }

    startPhotoDrag(event: MouseEvent, viewer: HTMLElement) {
        if (event.button !== 0) {
            return;
        }

        this.isDraggingPhoto = true;
        this.photoDragStartX = event.clientX;
        this.photoDragStartY = event.clientY;
        this.photoDragScrollLeft = viewer.scrollLeft;
        this.photoDragScrollTop = viewer.scrollTop;
        event.preventDefault();
    }

    movePhotoDrag(event: MouseEvent, viewer: HTMLElement) {
        if (!this.isDraggingPhoto) {
            return;
        }

        viewer.scrollLeft = this.photoDragScrollLeft - (event.clientX - this.photoDragStartX);
        viewer.scrollTop = this.photoDragScrollTop - (event.clientY - this.photoDragStartY);
        event.preventDefault();
    }

    stopPhotoDrag() {
        this.isDraggingPhoto = false;
    }

    getInitialOrderRequest(orderWrapper: CompleteOrder | undefined): NewOrderRequest | undefined {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.InitialOrderRequest;
    }

    getPickupAddress(orderWrapper: CompleteOrder | undefined): Address | undefined {
        const request = this.getInitialOrderRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.PickupAddress;
    }

    getDestinationAddress(orderWrapper: CompleteOrder | undefined): Address | undefined {
        const request = this.getInitialOrderRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.DestinationAddress;
    }

    getStops(orderWrapper: CompleteOrder | undefined): Address[] {
        const request = this.getInitialOrderRequest(orderWrapper);
        if (isNullOrUndefined(request) || isNullOrUndefined(request.Stops)) {
            return [];
        }

        return request.Stops;
    }

    shouldEditVoucherId() {
        return this.voucherEditMode === 'voucherId';
    }

    shouldEditVoucherIds() {
        return this.voucherEditMode === 'voucherIds';
    }

    setVoucherTab(tab: string) {
        this.activeVoucherTab = tab;
        this.stopPhotoDrag();
    }

    isVoucherTabActive(tab: string) {
        return this.activeVoucherTab === tab;
    }

    getOrderIdentifier(orderWrapper: CompleteOrder | undefined): string {
        const order = this.getOrder(orderWrapper);
        return !isNullOrUndefined(order) && !isNullOrUndefined(order.OrderIdentifier) ? order.OrderIdentifier : '-';
    }

    getOrderStartedDate(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        return !isNullOrUndefined(order) ? order.EntryDate : undefined;
    }

    getOrderFinishedDate(orderWrapper: CompleteOrder | undefined) {
        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseDate_iso)) {
            return orderWrapper.CloseDate_iso;
        }

        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseDate)) {
            return orderWrapper.CloseDate;
        }

        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(order) && !isNullOrUndefined(order.CloseDate)) {
            return order.CloseDate;
        }

        return undefined;
    }

    getOrderState(orderWrapper: CompleteOrder | undefined): string {
        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseReason)) {
            return orderWrapper.CloseReason;
        }

        return !isNullOrUndefined(order) && !isNullOrUndefined(order.CloseReason) ? order.CloseReason : '-';
    }

    getOrderPlatform(orderWrapper: CompleteOrder | undefined) {
        const request = this.getInitialOrderRequest(orderWrapper);
        return !isNullOrUndefined(request) && !isNullOrUndefined(request.DevicePlatform)
            ? getOrderSourceLabel(request.DevicePlatform)
            : '-';
    }

    getOrderCustomer(orderWrapper: CompleteOrder | undefined) {
        const request = this.getInitialOrderRequest(orderWrapper);
        if (isNullOrUndefined(request) || isNullOrUndefined(request.Customer)) {
            return '-';
        }

        return request.Customer.Name || request.Customer.PhoneNumber || request.Customer.Phone || '-';
    }

    getOrderAssignedCar(orderWrapper: CompleteOrder | undefined) {
        return formatOrderCar(this.getOrder(orderWrapper)?.AssignedCar);
    }

    getOrderCarNumber(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order) || isNullOrUndefined(order.AssignedCar)) {
            return '-';
        }

        return order.AssignedCar.CarNumber || '-';
    }

    getOrderDriverName(orderWrapper: CompleteOrder | undefined) {
        const driver = this.getOrderDriver(orderWrapper);
        return !isNullOrUndefined(driver) && !isNullOrUndefined(driver.DriverName) && driver.DriverName !== ''
            ? driver.DriverName
            : '-';
    }

    getOrderDriverPhone(orderWrapper: CompleteOrder | undefined) {
        const driver = this.getOrderDriver(orderWrapper);
        const driverPhone = !isNullOrUndefined(driver) ? this.normalizePhoneNumber(driver.DriverPhone) : '';
        return driverPhone !== ''
            ? driverPhone
            : '-';
    }

    normalizePhoneNumber(phoneNumber: unknown): string {
        if (isNullOrUndefined(phoneNumber)) {
            return '';
        }

        const trimmedPhoneNumber = String(phoneNumber).trim();
        const prefix = trimmedPhoneNumber.charAt(0) === '+' ? '+' : '';
        const digits = trimmedPhoneNumber.replace(/\D/g, '');

        return digits === '' ? '' : prefix + digits;
    }

    getOrderDriver(orderWrapper: CompleteOrder | undefined): Driver | undefined {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order) || isNullOrUndefined(order.AssignedCar)) {
            return undefined;
        }

        return order.AssignedCar.Driver;
    }

    getOrderPrice(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order)) {
            return '-';
        }

        const price = Number(order.TaximeterTotalPrice);
        return isNaN(price) || price === 0 ? '-' : price.toFixed(2) + ' lei';
    }

    getOrderDistance(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order)) {
            return '-';
        }

        const distance = Number(order.BillableDistance);
        return isNaN(distance) || distance === 0 ? '-' : (distance / 1000).toFixed(2) + ' km';
    }

    getRoutePoints(orderWrapper: CompleteOrder | undefined): VoucherRoutePoint[] {
        const points: VoucherRoutePoint[] = [];
        points.push({label: 'Pickup', address: this.getPickupAddress(orderWrapper), marker: 'P', type: 'pickup'});
        this.getStops(orderWrapper).forEach((stop, index) => {
            points.push({label: 'Stop ' + (index + 1), address: stop, marker: String(index + 1), type: 'stop'});
        });
        points.push({
            label: 'Destination',
            address: this.getDestinationAddress(orderWrapper),
            marker: 'D',
            type: 'destination'
        });

        return points.filter(point => !isNullOrUndefined(point.address));
    }

    formatAddress(address: Address | undefined) {
        if (isNullOrUndefined(address)) {
            return '-';
        }

        if (!isNullOrUndefined(address.CompleteAddress) && address.CompleteAddress !== '') {
            return address.CompleteAddress;
        }

        const parts = [];
        if (!isNullOrUndefined(address.StreetName) && address.StreetName !== '') {
            parts.push(address.StreetName);
        }
        if (!isNullOrUndefined(address.StreetNumber) && address.StreetNumber !== '') {
            parts.push('Nr. ' + address.StreetNumber);
        }
        if (!isNullOrUndefined(address.BlocNumber) && address.BlocNumber !== '') {
            parts.push('Bl. ' + address.BlocNumber);
        }
        if (!isNullOrUndefined(address.ScaraNumber) && address.ScaraNumber !== '') {
            parts.push('Sc. ' + address.ScaraNumber);
        }
        if (parts.length === 0 && !isNullOrUndefined(address.Locality) && address.Locality !== '') {
            parts.push(address.Locality);
        }

        return parts.length === 0 ? '-' : parts.join(', ');
    }

    private async showDeleteError(message: string): Promise<void> {
        await this.dialogService.alert({
            headerText: 'Error',
            bodyText: message,
            confirmButtonText: 'Close',
            danger: true
        });
    }

    private getVoucherCarFilterValue(voucher: PendingVoucher | undefined): string {
        if (isNullOrUndefined(voucher)) return '';
        return String(voucher.FullCarIdentifier || voucher.CarIdentifier || '').trim();
    }

    isPayMessageError() {
        return this.payMessageType === 'error';
    }

    fitSelectedOrderMapBounds() {
        if (!this.shouldFitSelectedOrderMap || !this.voucherOrderMap
            || this.selectedOrderMapMarkers.length < 2) {
            return;
        }

        const bounds = this.getMapBounds(this.selectedOrderMapMarkers);
        this.shouldFitSelectedOrderMap = false;
        this.voucherOrderMap.fitBounds(bounds, 48);
    }

    private getVouchersFromResponse(res: PendingVouchersResponse): PendingVoucher[] {
        if (!this.dataService.isResponseSuccessful(res)) {
            return [];
        }

        if (!isNullOrUndefined(res) && Array.isArray(res.PendingVouchers)) {
            return res.PendingVouchers;
        }

        return [];
    }

    private resetSelectedVoucher() {
        this.selectedVoucher = undefined;
        this.selectedVoucherSource = undefined;
        this.editableVoucherIds = '';
        this.voucherEditMode = '';
        this.activeVoucherTab = 'payment';
        this.clearSelectedOrderData();
        this.imageZoom = 1;
        this.stopPhotoDrag();
    }

    private setSelectedOrderData(voucher: PendingVoucher) {
        this.clearSelectedOrderData();
        this.selectedVoucherOrderWrapper = this.getVoucherOrderWrapper(voucher);

        if (isNullOrUndefined(this.selectedVoucherOrderWrapper)) {
            return;
        }

        this.selectedOrderSummary = {
            identifier: this.getOrderIdentifier(this.selectedVoucherOrderWrapper),
            started: this.getOrderStartedDate(this.selectedVoucherOrderWrapper),
            finished: this.getOrderFinishedDate(this.selectedVoucherOrderWrapper),
            state: this.getOrderState(this.selectedVoucherOrderWrapper),
            platform: this.getOrderPlatform(this.selectedVoucherOrderWrapper),
            customer: this.getOrderCustomer(this.selectedVoucherOrderWrapper),
            assignedCar: this.getOrderAssignedCar(this.selectedVoucherOrderWrapper),
            carNumber: this.getOrderCarNumber(this.selectedVoucherOrderWrapper),
            driverName: this.getOrderDriverName(this.selectedVoucherOrderWrapper),
            driverPhone: this.getOrderDriverPhone(this.selectedVoucherOrderWrapper),
            price: this.getOrderPrice(this.selectedVoucherOrderWrapper),
            distance: this.getOrderDistance(this.selectedVoucherOrderWrapper)
        };

        this.selectedOrderRoutePoints = this.getRoutePoints(this.selectedVoucherOrderWrapper)
            .map(point => ({
                label: point.label,
                address: point.address,
                formattedAddress: this.formatAddress(point.address),
                marker: point.marker,
                type: point.type
            }));

        this.selectedOrderMapMarkers = this.selectedOrderRoutePoints
            .map((point): VoucherMapMarker | undefined => {
                const coords = this.getAddressCoords(point.address);
                return isNullOrUndefined(coords) ? undefined : {
                    lat: coords.lat,
                    lng: coords.lng,
                    content: createRouteMarkerContent(
                        point.marker,
                        this.getRouteMarkerColor(point.type),
                        point.type === 'stop' ? '#111827' : '#ffffff'),
                    position: {lat: coords.lat, lng: coords.lng},
                    zIndex: 20,
                    title: point.label + ': ' + point.formattedAddress
                };
            })
            .filter((marker): marker is VoucherMapMarker => !isNullOrUndefined(marker));

        if (this.selectedOrderMapMarkers.length > 0) {
            this.centerOrderMap();
            this.selectedOrderMapZoom = this.selectedOrderMapMarkers.length > 1 ? 12 : 15;
            this.shouldFitSelectedOrderMap = this.selectedOrderMapMarkers.length > 1;
        }
    }

    private centerOrderMap() {
        const totals = this.selectedOrderMapMarkers.reduce((sum, marker) => ({
            lat: sum.lat + marker.lat,
            lng: sum.lng + marker.lng
        }), {lat: 0, lng: 0});

        this.selectedOrderMapLat = totals.lat / this.selectedOrderMapMarkers.length;
        this.selectedOrderMapLng = totals.lng / this.selectedOrderMapMarkers.length;
        this.selectedOrderMapCenter = {lat: this.selectedOrderMapLat, lng: this.selectedOrderMapLng};
    }

    private getRouteMarkerColor(type: string) {
        return type === 'pickup' ? '#16a34a'
            : type === 'destination' ? '#dc2626' : '#facc15';
    }

    private clearSelectedOrderData() {
        this.selectedVoucherOrderWrapper = undefined;
        this.selectedOrderSummary = undefined;
        this.selectedOrderRoutePoints = [];
        this.selectedOrderMapMarkers = [];
        this.selectedOrderMapLat = 44.429021;
        this.selectedOrderMapLng = 26.103849;
        this.selectedOrderMapCenter = {lat: this.selectedOrderMapLat, lng: this.selectedOrderMapLng};
        this.selectedOrderMapZoom = 13;
        this.selectedOrderInfoMarker = undefined;
        this.shouldFitSelectedOrderMap = false;
    }

    private getMapBounds(points: google.maps.LatLngLiteral[]): google.maps.LatLngBounds {
        const bounds = new google.maps.LatLngBounds();
        points.forEach(point => bounds.extend(point));
        return bounds;
    }

    private getAddressCoords(address: Address | undefined) {
        if (isNullOrUndefined(address)) {
            return undefined;
        }

        if (!isNullOrUndefined(address.Coords)) {
            return this.parseCoords(address.Coords);
        }

        return this.parseCoords(address as CoordinateLike);
    }

    private parseCoords(coords: CoordinateLike | undefined) {
        if (isNullOrUndefined(coords)) {
            return undefined;
        }

        const lat = Number(coords.GpsLat || coords.Latitude || coords.latitude || coords.Latitudine || coords.lat);
        const lng = Number(coords.GpsLong || coords.Longitude || coords.longitude || coords.Longitudine || coords.lng);

        if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
            return undefined;
        }

        return {lat, lng};
    }

    private setPayMessage(message: string, type: string) {
        this.payMessage = message;
        this.payMessageType = type;
    }

    private clearPayMessage() {
        this.payMessage = '';
        this.payMessageType = '';
    }

    private buildPayVoucherPayload(voucher: PendingVoucher): PendingVoucher {
        const payload = Object.assign({}, voucher);
        delete payload.FullCarIdentifier;
        const amount = Number(payload.Amount);

        payload.Amount = isNaN(amount) ? payload.Amount : amount;
        if (this.shouldEditVoucherId()) {
            payload.VoucherId = isNullOrUndefined(payload.VoucherId) ? '' : String(payload.VoucherId).trim();
            delete payload.VoucherIds;
        } else if (this.shouldEditVoucherIds()) {
            payload.VoucherIds = this.parseVoucherIds(this.editableVoucherIds);
            delete payload.VoucherId;
        } else {
            delete payload.VoucherId;
            delete payload.VoucherIds;
        }

        return payload;
    }

    private getVoucherEditMode(voucher: PendingVoucher | undefined) {
        if (!isNullOrUndefined(voucher) && !isNullOrUndefined(voucher.VoucherId) && String(voucher.VoucherId).trim() !== '') {
            return 'voucherId';
        }

        return this.getVoucherIds(voucher).length > 0 ? 'voucherIds' : '';
    }

    private formatVoucherIds(voucher: PendingVoucher | undefined) {
        return this.getVoucherIds(voucher).join('\n');
    }

    private parseVoucherIds(value: unknown): string[] {
        if (isNullOrUndefined(value)) {
            return [];
        }

        if (Array.isArray(value)) {
            return value.map(item => String(item).trim()).filter(item => item !== '');
        }

        return String(value)
            .split(/[\n,;]+/)
            .map(item => item.trim())
            .filter(item => item !== '');
    }

    private isPayResponseSuccessful(res: ApiResponse | string) {
        if (typeof res !== 'string' && this.dataService.isResponseSuccessful(res)) {
            return true;
        }

        if (typeof res === 'string') {
            try {
                return this.dataService.isResponseSuccessful(JSON.parse(res));
            } catch {
                return res.indexOf('"StatusCode":0') >= 0 || res.indexOf('StatusCode: 0') >= 0;
            }
        }

        return false;
    }

    private getResponseMessage(res: unknown, fallback: string) {
        const parsedResponse = this.parseResponse(res);
        const nestedErrorResponse = !isNullOrUndefined(parsedResponse) && !isNullOrUndefined(parsedResponse.error)
            ? this.parseResponse(parsedResponse.error)
            : null;
        const responseMessage = this.getMessageFromParsedResponse(nestedErrorResponse)
            || this.getMessageFromParsedResponse(parsedResponse);

        if (responseMessage !== '') {
            return responseMessage;
        }

        if (!isNullOrUndefined(parsedResponse) && parsedResponse.message
            && String(parsedResponse.message).trim() !== '') {
            return parsedResponse.message;
        }

        return fallback;
    }

    private getMessageFromParsedResponse(res: ResponsePayload | null) {
        if (!isNullOrUndefined(res) && !isNullOrUndefined(res.StatusMessage)
            && String(res.StatusMessage).trim() !== '') {
            return res.StatusMessage;
        }

        if (!isNullOrUndefined(res) && !isNullOrUndefined(res.Message)
            && String(res.Message).trim() !== '') {
            return res.Message;
        }

        return '';
    }

    private parseResponse(res: unknown): ResponsePayload | null {
        if (typeof res !== 'string') {
            return typeof res === 'object' && res !== null ? res as ResponsePayload : null;
        }

        try {
            return JSON.parse(res) as ResponsePayload;
        } catch {
            return null;
        }
    }
}
